import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { ArrowLeft, Send, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Autoavaliacao180 = () => {
  const navigate = useNavigate();
  const { cicloId } = useParams();
  
  const [loading, setLoading] = useState(false);
  const [ciclo, setCiclo] = useState(null);
  const [competencias, setCompetencias] = useState([]);
  const [respostas, setRespostas] = useState({});
  const [pontosFortes, setPontosFortes] = useState('');
  const [areasMelhoria, setAreasMelhoria] = useState('');
  const [progresso, setProgresso] = useState(0);

  useEffect(() => {
    fetchCicloECompetencias();
  }, [cicloId]);

  useEffect(() => {
    calcularProgresso();
  }, [respostas, competencias]);

  const fetchCicloECompetencias = async () => {
    try {
      const [cicloRes, compRes] = await Promise.all([
        axios.get(`${API}/api/feedback-180/ciclos/${cicloId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/api/feedback-180/competencias`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      setCiclo(cicloRes.data);
      
      const competenciasCiclo = compRes.data.filter(c => 
        cicloRes.data.competencias_ids.includes(c.id)
      );
      setCompetencias(competenciasCiclo);

      // Buscar autoavaliação existente
      const autoRes = await axios.get(`${API}/api/feedback-180/autoavaliacao/ciclo/${cicloId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (autoRes.data) {
        const respostasMap = {};
        autoRes.data.respostas.forEach(r => {
          respostasMap[r.competencia_id] = {
            nota: r.nota,
            comentario: r.comentario || ''
          };
        });
        setRespostas(respostasMap);
        setPontosFortes(autoRes.data.pontos_fortes || '');
        setAreasMelhoria(autoRes.data.areas_melhoria || '');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao carregar dados');
    }
  };

  const calcularProgresso = () => {
    if (competencias.length === 0) return;
    const respondidas = Object.keys(respostas).length;
    const percentual = (respondidas / competencias.length) * 100;
    setProgresso(Math.round(percentual));
  };

  const handleNotaChange = (compId, nota) => {
    setRespostas(prev => ({
      ...prev,
      [compId]: { ...prev[compId], nota, comentario: prev[compId]?.comentario || '' }
    }));
  };

  const handleComentarioChange = (compId, comentario) => {
    setRespostas(prev => ({
      ...prev,
      [compId]: { ...prev[compId], comentario, nota: prev[compId]?.nota || 0 }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (Object.keys(respostas).length < competencias.length) {
      toast.error('Por favor, avalie todas as competências');
      return;
    }

    try {
      setLoading(true);

      const respostasArray = Object.entries(respostas).map(([compId, dados]) => ({
        competencia_id: compId,
        nota: dados.nota,
        comentario: dados.comentario
      }));

      await axios.post(`${API}/api/feedback-180/autoavaliacao`, {
        ciclo_id: cicloId,
        respostas: respostasArray,
        pontos_fortes: pontosFortes,
        areas_melhoria: areasMelhoria
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Autoavaliação enviada com sucesso!');
      navigate('/feedback/cultural');
    } catch (error) {
      toast.error('Erro ao enviar autoavaliação');
    } finally {
      setLoading(false);
    }
  };

  if (!ciclo) {
    return <div className="p-6">Carregando...</div>;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Button variant="outline" onClick={() => navigate('/feedback/cultural')} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Voltar
      </Button>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Autoavaliação - {ciclo.nome}</CardTitle>
          <CardDescription>
            Este é o momento de refletir sobre seu desempenho e comportamentos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progresso</span>
              <span className="font-semibold">{progresso}%</span>
            </div>
            <Progress value={progresso} className="h-2" />
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-6">
        {competencias.map((comp, index) => (
          <Card key={comp.id}>
            <CardHeader>
              <CardTitle className="text-lg">
                {index + 1}. {comp.nome}
              </CardTitle>
              <CardDescription>{comp.descricao}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Como você se avalia nesta competência?</Label>
                <div className="flex items-center space-x-2">
                  {[1, 2, 3, 4, 5].map((nota) => (
                    <button
                      key={nota}
                      type="button"
                      onClick={() => handleNotaChange(comp.id, nota)}
                      className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                        respostas[comp.id]?.nota === nota
                          ? 'bg-purple-600 text-white border-purple-600 scale-110'
                          : 'border-gray-300 hover:border-purple-400'
                      }`}
                    >
                      {nota}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-600">
                  1 = Discordo totalmente | 5 = Concordo totalmente
                </p>
              </div>

              {ciclo.permitir_comentarios && (
                <div className="space-y-2">
                  <Label>Comentário (opcional)</Label>
                  <Textarea
                    value={respostas[comp.id]?.comentario || ''}
                    onChange={(e) => handleComentarioChange(comp.id, e.target.value)}
                    placeholder="Adicione observações sobre esta competência..."
                    rows={2}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        <Card>
          <CardHeader>
            <CardTitle>Reflexão Geral</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>O que você acredita que tem feito bem?</Label>
              <Textarea
                value={pontosFortes}
                onChange={(e) => setPontosFortes(e.target.value)}
                placeholder="Descreva seus pontos fortes e conquistas..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>O que você gostaria de melhorar?</Label>
              <Textarea
                value={areasMelhoria}
                onChange={(e) => setAreasMelhoria(e.target.value)}
                placeholder="Identifique áreas de desenvolvimento..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4">
          <Button type="button" variant="outline" onClick={() => navigate('/feedback/cultural')}>
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading || progresso < 100}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Send className="h-4 w-4 mr-2" />
            {loading ? 'Enviando...' : 'Enviar Autoavaliação'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Autoavaliacao180;
